if (!customElements.get('bbc-logo-customizer')) {
  customElements.define(
    'bbc-logo-customizer',
    class BbcLogoCustomizer extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.productFormId;
        this.form = document.getElementById(this.formId);
        if (!this.form) return;

        this.productForm = this.form.closest('product-form');
        this.details = this.querySelector('[data-bbc-logo-details]');
        this.errorBox = this.querySelector('[data-bbc-logo-error]');
        this.quantityInput = this.querySelector('[data-bbc-quantity]');
        this.uploadInput = this.querySelector('[data-bbc-native-upload]');
        this.uploadStatus = this.querySelector('[data-bbc-upload-status]');
        this.uploadError = this.querySelector('[data-bbc-upload-error]');
        this.boxPriceProperty = this.querySelector('[data-bbc-box-price-property]');
        this.nativeQuantityInput = document.querySelector(`input.quantity__input[form="${CSS.escape(this.formId)}"]`);
        if (this.nativeQuantityInput && this.nativeQuantityInput !== this.quantityInput) {
          this.nativeQuantityInput.removeAttribute('name');
          this.nativeQuantityInput.closest('.product-form__quantity')?.setAttribute('hidden', '');
        }
        this.variants = JSON.parse(this.querySelector('[data-bbc-product-variants]').textContent);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.productForm?.querySelector('[type="submit"]');
        this.submitButtonLabel = this.submitButton?.querySelector('span') || this.submitButton;
        this.originalSubmitText = this.submitButtonLabel?.textContent.trim() || 'Add to cart';
        this.originalSubmitDisabled = Boolean(this.submitButton?.disabled);
        this.originalSubmitAriaDisabled = this.submitButton?.getAttribute('aria-disabled');

        const guideDialog = this.querySelector('[data-bbc-guide-dialog]');
        this.querySelectorAll('[data-bbc-guide-open]').forEach((control) => {
          control.addEventListener('click', (event) => {
            if (!guideDialog?.showModal) return;
            event.preventDefault();
            guideDialog.showModal();
          });
        });
        this.querySelector('[data-bbc-guide-close]')?.addEventListener('click', () => guideDialog?.close());
        guideDialog?.addEventListener('click', (event) => {
          if (event.target === guideDialog) guideDialog.close();
        });
        this.querySelector('[data-bbc-method-disclosure]')?.addEventListener('toggle', () =>
          this.updateMethodDisclosure()
        );

        this.addEventListener('change', this.onCustomizerChange.bind(this));
        this.quantityInput?.addEventListener('input', this.updateSummary.bind(this));
        this.quantityInput?.addEventListener('change', this.normalizeQuantity.bind(this));
        this.quantityInput?.addEventListener('blur', this.normalizeQuantity.bind(this));
        this.uploadInput?.addEventListener('change', this.onArtworkChange.bind(this));
        this.querySelectorAll('[data-bbc-quantity-change]').forEach((button) => {
          button.addEventListener('click', () => {
            const minimum = this.dataset.paperDrawer === 'true' && !this.logoSelected ? 1 : Number(this.dataset.minimumQuantity || 20);
            const delta = Number(button.dataset.bbcQuantityChange || 0);
            this.quantityInput.value = String(Math.max(minimum, Number(this.quantityInput.value || minimum) + delta));
            this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          });
        });
        this.form.addEventListener('submit', this.onSubmit.bind(this), true);
        this.form.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('[type="submit"]')) this.normalizeQuantity();
          },
          true
        );
        document.addEventListener('change', (event) => {
          if (event.target.closest(`#${CSS.escape(this.formId)}`) || event.target.name?.startsWith('options[')) {
            window.setTimeout(() => this.updateSummary(), 0);
          }
        });

        this.syncRequiredState();
        this.updateSummary();
      }

      disconnectedCallback() {
      }

      get logoSelected() {
        return this.querySelector('[name="properties[Logo Printing]"]:checked')?.value === 'Custom Logo';
      }

      get selectedPlacements() {
        return Array.from(this.querySelectorAll('[name="properties[Logo Placement]"]:checked')).map(
          (control) => control.value
        );
      }

      get locationCount() {
        return this.selectedPlacements.length;
      }

      get selectedMethod() {
        return this.querySelector('[name="properties[Printing Method]"]:checked')?.value || '';
      }

      get quantity() {
        const minimum = this.dataset.paperDrawer === 'true' && !this.logoSelected ? 1 : Number(this.dataset.minimumQuantity || 20);
        return Math.max(minimum, Number(this.quantityInput?.value || minimum));
      }

      feeForQuantity(quantity) {
        if (quantity <= 20) {
          return {
            variantId: this.dataset.feeTwenty,
            lineQuantity: 1,
            cents: 7000,
            label: 'Up to 20 boxes — $70 per location',
          };
        }
        if (quantity <= 50) {
          return {
            variantId: this.dataset.feeTwentyOneToFifty,
            lineQuantity: 1,
            cents: 10000,
            label: '21–50 boxes — $100 per location',
          };
        }
        if (quantity <= 100) {
          return {
            variantId: this.dataset.feeFiftyOneToHundred,
            lineQuantity: 1,
            cents: 13000,
            label: '51–100 boxes — $130 per location',
          };
        }
        if (quantity <= 200) {
          return {
            variantId: this.dataset.feeOneOhOneToTwoHundred,
            lineQuantity: 1,
            cents: 17000,
            label: '101–200 boxes — $170 per location',
          };
        }
        if (quantity <= 500) {
          return {
            variantId: this.dataset.feeTwoOhOneToFiveHundred,
            lineQuantity: quantity,
            cents: quantity * 95,
            label: '201–500 boxes — $0.95 per box, per location',
          };
        }
        return {
          variantId: this.dataset.feeFiveOhOnePlus,
          lineQuantity: quantity,
          cents: quantity * 80,
          label: '501+ boxes — $0.80 per box, per location',
        };
      }

      money(cents) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
      }

      selectedVariant() {
        const id = Number(this.form.querySelector('[name="id"]')?.value);
        return this.variants.find((variant) => Number(variant.id) === id) || this.variants[0];
      }

      onCustomizerChange(event) {
        if (event.target.name === 'properties[Logo Printing]') {
          this.details.hidden = !this.logoSelected;
          if (this.logoSelected && !this.selectedMethod) {
            this.querySelector('[data-bbc-method-disclosure]')?.setAttribute('open', '');
          }
          this.syncRequiredState();
          if (this.dataset.paperDrawer === 'true') {
            this.quantityInput.min = this.logoSelected ? String(this.dataset.minimumQuantity || 20) : '1';
            this.normalizeQuantity();
          }
        }
        if (event.target.name === 'properties[Printing Method]') {
          this.updateMethodDisclosure(true);
        }
        this.clearError();
        this.updateSummary();
      }

      syncRequiredState() {
        this.querySelectorAll('[data-bbc-required]').forEach((control) => {
          control.required = this.logoSelected;
        });
      }

      updateMethodDisclosure(collapseAfterSelection = false) {
        const disclosure = this.querySelector('[data-bbc-method-disclosure]');
        const summary = this.querySelector('[data-bbc-method-summary]');
        const action = this.querySelector('[data-bbc-method-action]');
        if (!disclosure || !summary || !action) return;

        if (this.selectedMethod) {
          summary.textContent = `Selected: ${this.selectedMethod}`;
          action.textContent = 'Change';
          if (collapseAfterSelection) window.setTimeout(() => disclosure.removeAttribute('open'), 120);
        } else {
          summary.textContent = 'Select one of five printing methods';
          action.textContent = disclosure.open ? 'Hide options' : 'View options';
        }
      }

      normalizeQuantity() {
        const minimum = this.dataset.paperDrawer === 'true' && !this.logoSelected ? 1 : Number(this.dataset.minimumQuantity || 20);
        if (!Number.isFinite(Number(this.quantityInput.value)) || Number(this.quantityInput.value) < minimum) {
          this.quantityInput.value = String(minimum);
        }
        this.updateSummary();
      }

      artworkValidationError(file) {
        if (!file) return 'Please choose a logo file.';
        const extension = file.name.toLowerCase().split('.').pop();
        const allowedExtensions = ['ai', 'eps', 'svg', 'pdf', 'png', 'jpg', 'jpeg'];
        if (!allowedExtensions.includes(extension)) {
          return 'Use an AI, EPS, SVG, PDF, PNG, JPG, or JPEG file.';
        }
        const maxArtworkMb = this.dataset.paperDrawer === 'true' ? 20 : 10;
        if (file.size > maxArtworkMb * 1024 * 1024) {
          return `The logo file must be ${maxArtworkMb} MB or smaller.`;
        }
        return '';
      }

      onArtworkChange() {
        const file = this.uploadInput?.files?.[0];
        const error = file ? this.artworkValidationError(file) : '';
        if (error && this.uploadInput) this.uploadInput.value = '';

        if (this.uploadError) {
          this.uploadError.textContent = error;
          this.uploadError.hidden = !error;
        }
        if (this.uploadStatus) {
          const selectedFile = this.uploadInput?.files?.[0];
          this.uploadStatus.textContent = selectedFile
            ? `Selected: ${selectedFile.name} · ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
            : 'No file selected.';
        }

        this.clearError();
        this.updatePurchaseState();
      }

      updateSummary() {
        if (this.boxPriceProperty && this.dataset.paperDrawer === 'true') {
          this.boxPriceProperty.disabled = !this.logoSelected;
        }
        const quantity = this.quantity;
        const variant = this.selectedVariant();
        const unitCents = Number(variant?.price || 0);
        const boxCents = unitCents * quantity;
        if (this.boxPriceProperty) {
          this.boxPriceProperty.value = `${this.money(unitCents)} each × ${quantity} = ${this.money(boxCents)}`;
        }
        const fee = this.feeForQuantity(quantity);

        const quantitySnapshot = this.querySelector('[data-bbc-quantity-snapshot]');
        if (quantitySnapshot) {
          quantitySnapshot.textContent = `${quantity} ${this.dataset.paperDrawer === 'true' && quantity === 1 ? 'box' : 'boxes'} · Box subtotal ${this.money(boxCents)}`;
        }

        const currentTier = this.querySelector('[data-bbc-current-tier]');
        if (currentTier) {
          currentTier.textContent = `Current tier: ${fee.label}`;
        }

        this.updateMethodDisclosure();

        if (!this.logoSelected) {
          this.updatePurchaseState();
          return;
        }

        this.querySelector('[data-bbc-box-subtotal]').textContent = this.money(boxCents);
        this.querySelector('[data-bbc-print-method]').textContent = this.selectedMethod || 'Select a method';
        if (!this.locationCount) {
          this.querySelector('[data-bbc-logo-fee]').textContent = 'Select a print location';
          this.querySelector('[data-bbc-estimated-total]').textContent = '—';
          this.querySelector('[data-bbc-fee-tier]').textContent = `Price per location: ${fee.label}`;
          this.updatePurchaseState();
          return;
        }

        const totalFeeCents = fee.cents * this.locationCount;
        this.querySelector('[data-bbc-logo-fee]').textContent = this.money(totalFeeCents);
        this.querySelector('[data-bbc-estimated-total]').textContent = this.money(boxCents + totalFeeCents);
        this.querySelector('[data-bbc-fee-tier]').textContent = `${this.locationCount} print location${
          this.locationCount === 1 ? '' : 's'
        } selected · ${fee.label}`;
        this.updatePurchaseState();
      }

      uploaderHasFile() {
        const file = this.uploadInput?.files?.[0];
        return Boolean(file && !this.artworkValidationError(file));
      }

      updatePurchaseState() {
        if (!this.submitButton || !this.submitButtonLabel) return;
        const completion = this.querySelector('[data-bbc-completion]');

        if (!this.logoSelected) {
          if (completion) completion.hidden = true;
          this.releaseSubmitButton();
          return;
        }

        const permission = this.querySelector('[name="properties[_Artwork Permission Confirmed]"]')?.checked;
        let message = 'Ready to add to cart.';
        let complete = true;

        if (!this.locationCount) {
          message = 'Select at least one print location in Step 2 to continue.';
          complete = false;
        } else if (!this.selectedMethod) {
          message = 'Choose a printing method in Step 3 to continue.';
          complete = false;
        } else if (!this.uploaderHasFile()) {
          message = 'Upload your logo file in Step 4 to continue.';
          complete = false;
        } else if (!permission) {
          message = 'Confirm your permission to use the artwork in Step 4 to continue.';
          complete = false;
        }

        if (completion) {
          completion.hidden = false;
          completion.textContent = message;
          completion.classList.toggle('is-complete', complete);
        }

        if (complete) {
          this.releaseSubmitButton();
        } else {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.dataset.bbcDisabledByCustomizer = 'true';
          this.submitButtonLabel.textContent = 'Complete the required steps above';
        }
      }

      releaseSubmitButton() {
        if (!this.submitButton?.dataset.bbcDisabledByCustomizer) return;
        const variantAvailable = this.selectedVariant()?.available !== false;
        this.submitButton.disabled = this.originalSubmitDisabled || !variantAvailable;
        if (this.originalSubmitAriaDisabled === null && variantAvailable && !this.originalSubmitDisabled) {
          this.submitButton.removeAttribute('aria-disabled');
        } else {
          this.submitButton.setAttribute('aria-disabled', this.originalSubmitAriaDisabled || 'true');
        }
        this.submitButtonLabel.textContent = this.originalSubmitText;
        delete this.submitButton.dataset.bbcDisabledByCustomizer;
      }

      validate() {
        this.syncRequiredState();
        if (!this.locationCount) return 'Please select at least one logo placement.';
        if (!this.selectedMethod) return 'Please select a printing method.';
        if (!this.uploadInput?.files?.length) return 'Please upload your logo file before adding this item to the cart.';
        const artworkError = this.artworkValidationError(this.uploadInput.files[0]);
        if (artworkError) return artworkError;
        const permission = this.querySelector('[name="properties[_Artwork Permission Confirmed]"]');
        if (!permission.checked) return 'Please confirm that you have permission to use the uploaded artwork.';
        return '';
      }

      setError(message) {
        this.errorBox.textContent = message;
        this.errorBox.hidden = false;
        this.errorBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      clearError() {
        this.errorBox.textContent = '';
        this.errorBox.hidden = true;
      }

      lineProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match || value instanceof File || value === '') continue;
          properties[match[1]] = value;
        }
        return properties;
      }

      async onSubmit(event) {
        this.nativeQuantityInput?.removeAttribute('name');
        this.normalizeQuantity();
        if (!this.logoSelected) {
          const hiddenLogoFields = Array.from(
            document.querySelectorAll(
              '[name="properties[add_custom_logo]"], [name="properties[Logo Placement]"], [name="properties[Printing Method]"], [name="properties[Artwork Notes]"], [name="properties[_Artwork Permission Confirmed]"], [name="properties[logo_file]"]'
            )
          ).filter((field) => field.form === this.form);
          hiddenLogoFields.forEach((field) => {
            field.disabled = true;
          });
          window.setTimeout(() => {
            hiddenLogoFields.forEach((field) => {
              field.disabled = false;
            });
          }, 0);
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.clearError();

        const validationError = this.validate();
        if (validationError) {
          this.setError(validationError);
          return;
        }

        const button = this.productForm?.querySelector('[type="submit"]');
        if (button?.getAttribute('aria-disabled') === 'true') return;
        button?.setAttribute('aria-disabled', 'true');
        button?.classList.add('loading');
        this.productForm?.querySelector('.loading__spinner')?.classList.remove('hidden');

        const quantity = this.quantity;
        const fee = this.feeForQuantity(quantity);
        const placements = this.selectedPlacements;
        const printingMethod = this.selectedMethod;
        const customerPrintingMethod = printingMethod.replace(/^[A-E] - /, '');
        const locationCount = placements.length;
        const placementLabel = placements.join(' + ');
        const checkoutPlacementLabel = placements.map((placement) => placement.replace(' - ', ' — ')).join(' + ');
        const totalFeeCents = fee.cents * locationCount;
        const groupId = `bbc-logo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const formData = new FormData(this.form);
        const artwork = formData.get('properties[logo_file]');
        if (artwork instanceof File && artwork.name) {
          formData.set('properties[_logo_file]', artwork, artwork.name);
        } else if (typeof artwork === 'string' && artwork.trim()) {
          formData.set('properties[_logo_file]', artwork.trim());
        }
        formData.delete('properties[logo_file]');
        formData.delete('properties[add_custom_logo]');
        formData.delete('properties[Logo Placement]');
        formData.delete('properties[Printing Method]');
        formData.set('quantity', String(quantity));
        const unitCents = Number(this.selectedVariant()?.price || 0);
        formData.set(
          'properties[Box price]',
          `${this.money(unitCents)} each × ${quantity} = ${this.money(unitCents * quantity)}`
        );
        formData.set('properties[Logo Placement]', checkoutPlacementLabel);
        formData.set('properties[Printing Method]', customerPrintingMethod);
        formData.set('properties[_bbc_logo_group]', groupId);
        formData.set('properties[_bbc_logo_role]', 'base');
        formData.set('properties[_bbc_logo_location_count]', String(locationCount));

        let baseItem;
        try {
          const baseResponse = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          baseItem = await baseResponse.json();
          if (!baseResponse.ok || baseItem.status) throw new Error(baseItem.description || 'The box could not be added.');

          const feeProperties = {
            'Applies to': `${quantity} boxes`,
            'Print locations': `${locationCount} — ${checkoutPlacementLabel}`,
            'Fee tier': fee.label,
            'Printing total': `${this.money(fee.cents)} per location × ${locationCount} = ${this.money(totalFeeCents)}`,
            '_Printing method': customerPrintingMethod,
            '_Number of print locations': String(locationCount),
            '_Calculated printing fee': this.money(totalFeeCents),
            '_bbc_logo_group': groupId,
            '_bbc_logo_role': 'fee',
            '_bbc_logo_base_key': baseItem.key,
            '_bbc_logo_location_count': String(locationCount),
          };

          const feePayload = {
            items: [{ id: Number(fee.variantId), quantity: fee.lineQuantity * locationCount, properties: feeProperties }],
          };
          if (this.cart) {
            feePayload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            feePayload.sections_url = window.location.pathname;
            this.cart.setActiveElement?.(document.activeElement);
          }

          const feeResponse = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(feePayload),
          });
          const feeResult = await feeResponse.json();
          if (!feeResponse.ok || feeResult.status) throw new Error(feeResult.description || 'The printing fee could not be added.');

          if (this.cart) {
            try {
              this.cart.renderContents(feeResult);
            } catch (renderError) {
              window.location.assign(window.routes.cart_url);
            }
          } else {
            window.location.assign(window.routes.cart_url);
          }
        } catch (error) {
          if (baseItem?.key) {
            await fetch(`${routes.cart_change_url}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: baseItem.key, quantity: 0 }),
            }).catch(() => {});
          }
          this.setError(error.message || 'The customized item could not be added. Please try again.');
        } finally {
          button?.classList.remove('loading');
          button?.removeAttribute('aria-disabled');
          this.productForm?.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }
    }
  );
}
